import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import { CalibrationDialog } from "@/components/CalibrationDialog";
import { saveDefaults } from "@/lib/defaults";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { Settings, Save, ArrowLeft, MonitorCheck, Ruler, Building2, PenTool, Lock, ShieldCheck, Languages, Check, Pencil, Gift } from "lucide-react";

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        {desc && <p className="text-sm text-slate-500">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

const ReadOnly = ({ value }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 min-h-[38px] flex items-center">
    {value || <span className="text-slate-400">—</span>}
  </div>
);

// Un asset già salvato ma non consegnato dal server: `{present: true}` senza dataUrl.
// Rimandarlo indietro invariato significa "tieni quello che hai già".
const isWithheld = (v) => !!(v && v.present && !v.dataUrl);

export const Configuration = ({ mode, licenseConfig, entitlement = {}, data, update, onSaveConfig, onClose }) => {
  const { t, lang, setLang, languages } = useI18n();
  const isSetup = mode === "setup";
  // La configurazione resta correggibile finché non è stato emesso alcun referto: si
  // imposta prima del pagamento, e un refuso non deve restare per sempre su un account
  // che non ha ancora versato nulla.
  const canEdit = isSetup || entitlement.configEditable === true;
  const pxPerMm = data.calibration?.pxPerMm || null;
  const cfg = licenseConfig || {};

  const [form, setForm] = useState({
    strutturaNome: cfg.strutturaNome || "",
    strutturaIndirizzo: cfg.strutturaIndirizzo || "",
    medico: cfg.medico || "",
    logo: cfg.logo || null,
    firma: cfg.firma || null,
  });
  const [saving, setSaving] = useState(false);
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const assetField = (key, testId, previewClass) => {
    const stored = cfg[key];
    if (canEdit) {
      // Un asset trattenuto non ha anteprima da mostrare: si offre di sostituirlo,
      // e se non lo si tocca il server conserva quello già salvato.
      if (isWithheld(form[key])) {
        return (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
              <Check className="w-4 h-4" /> {t("img_uploaded")}
            </span>
            <Button type="button" variant="outline" size="sm" data-testid={`${testId}-replace`}
              onClick={() => setF({ [key]: null })}>
              {t("img_replace")}
            </Button>
          </div>
        );
      }
      return <ImageUpload value={form[key]} onChange={(v) => setF({ [key]: v })} testId={testId} previewClass={previewClass} />;
    }
    if (stored?.dataUrl)
      return <img src={stored.dataUrl} alt={key} className={`${previewClass} w-auto max-w-[220px] object-contain rounded-md border border-slate-200 bg-white p-1.5`} />;
    // Senza dataURL ci si affida a has_logo / has_firma: la schermata sa che l'asset
    // esiste anche senza possederne il file.
    const present = key === "logo" ? entitlement.hasLogo : entitlement.hasFirma;
    return present
      ? <span className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
          <Check className="w-4 h-4" /> {t("img_uploaded")}
        </span>
      : <ReadOnly value="" />;
  };

  const onCalibrated = (ppm) => {
    update("calibration", { pxPerMm: ppm });
    saveDefaults({ calibrationPxPerMm: ppm });
  };

  const handleSave = async () => {
    if (!form.strutturaNome.trim() || !form.medico.trim()) {
      toast.error(t("cfg_req_error"));
      return;
    }
    setSaving(true);
    await onSaveConfig(form);
    setSaving(false);
  };

  return (
    <div className="step-fade" data-testid="configuration-view">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <Settings className="w-6 h-6" strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {isSetup ? t("cfg_setup_title") : t("cfg_title")}
            </h2>
            <p className="text-slate-500">{isSetup ? t("cfg_setup_sub") : canEdit ? t("cfg_editable_sub") : t("cfg_locked_sub")}</p>
          </div>
        </div>
        {!isSetup && (
          <Button variant="outline" onClick={onClose} data-testid="config-close-button">
            <ArrowLeft className="w-4 h-4" /> {t("cfg_back")}
          </Button>
        )}
      </div>

      {isSetup && (
        <div className="mb-5 flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="config-free-note">
          <Gift className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-900">{t("cfg_setup_free_note")}</p>
        </div>
      )}

      {!isSetup && canEdit && (
        <div className="mb-5 flex items-center gap-3 rounded-md border border-sky-200 bg-sky-50 p-4" data-testid="config-editable-notice">
          <Pencil className="w-5 h-5 text-sky-600 flex-shrink-0" />
          <p className="text-sm text-sky-900">{t("cfg_editable_notice")}</p>
        </div>
      )}

      {!isSetup && !canEdit && (
        <div className="mb-5 flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="config-locked-notice">
          <Lock className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-900">{t("cfg_locked_notice")}</p>
        </div>
      )}

      <div className="space-y-5">
        <Section icon={Languages} title={t("language_label")} desc="">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-full sm:w-64" data-testid="config-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {languages.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Section>

        <Section icon={Building2} title={t("cfg_struct_title")} desc={t("cfg_struct_desc")}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={t("cfg_name_label")} required={canEdit}>
              {canEdit ? (
                <Input data-testid="config-struttura-nome" value={form.strutturaNome}
                  onChange={(e) => setF({ strutturaNome: e.target.value })} placeholder={t("cfg_name_ph")} />
              ) : <ReadOnly value={cfg.strutturaNome} />}
            </Field>
            <Field label={t("cfg_addr_label")}>
              {canEdit ? (
                <Input data-testid="config-struttura-indirizzo" value={form.strutturaIndirizzo}
                  onChange={(e) => setF({ strutturaIndirizzo: e.target.value })} placeholder={t("cfg_addr_ph")} />
              ) : <ReadOnly value={cfg.strutturaIndirizzo} />}
            </Field>
          </div>
          <div className="mt-6">
            <Field label={t("cfg_logo_label")}>
              {assetField("logo", "config-logo-upload", "h-16")}
            </Field>
          </div>
        </Section>

        <Section icon={ShieldCheck} title={t("cfg_doctor_title")} desc={t("cfg_doctor_desc")}>
          <Field label={t("cfg_doctor_label")} required={canEdit}>
            {canEdit ? (
              <Input data-testid="config-medico" value={form.medico}
                onChange={(e) => setF({ medico: e.target.value })} placeholder={t("cfg_doctor_ph")} />
            ) : <ReadOnly value={cfg.medico} />}
          </Field>
        </Section>

        <Section icon={PenTool} title={t("cfg_sign_title")} desc={t("cfg_sign_desc")}>
          {assetField("firma", "config-firma-upload", "h-20")}
        </Section>

        <Section icon={pxPerMm ? MonitorCheck : Ruler} title={t("cfg_calib_title")} desc={t("cfg_calib_desc")}>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium" data-testid="config-calibration-status">
              {pxPerMm
                ? <span className="text-emerald-700">{t("cfg_calib_ok")} — {pxPerMm.toFixed(2)} px/mm</span>
                : <span className="text-amber-600">{t("cfg_calib_no")}</span>}
            </span>
            <CalibrationDialog
              pxPerMm={pxPerMm}
              onCalibrated={onCalibrated}
              trigger={
                <Button variant="outline" data-testid="config-open-calibration">
                  <Ruler className="w-4 h-4" /> {pxPerMm ? t("cfg_recalib") : t("cfg_calib_btn")}
                </Button>
              }
            />
          </div>
        </Section>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="config-save-button" className="bg-primary hover:bg-primary/90 transition-colors">
              <Save className="w-4 h-4" /> {saving ? t("cfg_saving") : isSetup ? t("cfg_save_btn") : t("cfg_edit_save_btn")}
            </Button>
            <span className="text-xs text-slate-400">{t("cfg_save_warn")}</span>
          </div>
        )}
      </div>
    </div>
  );
};
